import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { primaryLabel } from './commercialFormat';
import { buildConfigBody, buildSecretsBody, fieldsForKind } from './connectorPayload';
import { RefreshCw, AlertTriangle, CheckCircle2, Plug, Lock, KeyRound, Power, Trash2, Activity, Settings2, Plus } from 'lucide-react';

/**
 * SACP — Tenant-safe Integrations management (Business plane).
 *
 * Talks ONLY to the server-derived, secret-isolated tenant connector API
 * (/api/v1/billing/me/connectors/*): tenant resolved from the token (no client tenant id → no
 * cross-tenant read), secret VALUES are write-only and never returned (the UI shows secret STATUS
 * only), and every Create/Configure/Credentials/Enable/Disable/Test/Delete action calls a real
 * mounted endpoint that enforces entitlement + slot capacity server-side and re-reads afterwards.
 * A control is shown only when its backend action is permitted; otherwise it is hidden or disabled
 * (never a dead button). No API secret, secret name value, or provider-global credential is displayed.
 *
 * @param request  makeAuthenticatedRequest(url, opts) -> fetch Response (tenant Firebase token)
 */
const jsonPost = (request, url, body) =>
  request(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
const jsonPatch = (request, url, body) =>
  request(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

export default function TenantIntegrations({ request }) {
  const [state, setState] = useState('loading');   // loading | ok | expired | error
  const [catalog, setCatalog] = useState([]);
  const [instances, setInstances] = useState([]);
  const [slots, setSlots] = useState({ used: 0, allowed: 0 });
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);          // instance_id/action currently running
  const [editing, setEditing] = useState(null);    // { instance, kind:'config'|'secrets' } | null
  const [fields, setFields] = useState({});
  const [createFor, setCreateFor] = useState(null); // connector row being added
  const [nickname, setNickname] = useState('');
  const [retention, setRetention] = useState(null);   // downgrade grace / over-limit state
  const [selectOpen, setSelectOpen] = useState(false);
  const [selected, setSelected] = useState([]);       // instance_ids the tenant chooses to retain

  const load = useCallback(async () => {
    setState('loading'); setToast(null);
    try {
      const [c, i, rt] = await Promise.all([
        request('/api/v1/billing/me/connectors'),
        request('/api/v1/billing/me/connectors/instances'),
        request('/api/v1/billing/me/connectors/retention-state'),
      ]);
      if ([c, i, rt].some((r) => r && r.status === 401)) { setState('expired'); return; }
      if (!c || !c.ok) { setState('error'); return; }
      const cData = await c.json();
      setCatalog(cData.connectors || []);
      setSlots({ used: cData.active_slots_used, allowed: cData.active_slots_allowed });
      setInstances(i && i.ok ? (await i.json()).instances || [] : []);
      setRetention(rt && rt.ok ? await rt.json() : null);
      setState('ok');
    } catch (_) { setState('error'); }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  const run = async (action, id, fn, okMsg) => {
    setBusy(`${id}:${action}`); setToast(null);
    try {
      const r = await fn();
      if (r && r.status === 401) { setState('expired'); return; }
      if (!r || !r.ok) {
        const d = r ? await r.json().catch(() => ({})) : {};
        const code = d?.detail?.code || d?.detail || `HTTP ${r ? r.status : '—'}`;
        setToast({ ok: false, msg: typeof code === 'string' ? code : JSON.stringify(code) });
        return null;
      }
      const data = await r.json().catch(() => ({}));
      if (okMsg) setToast({ ok: true, msg: okMsg });
      await load();
      return data;
    } catch (_) { setToast({ ok: false, msg: 'Network error. Please retry.' }); return null; }
    finally { setBusy(null); }
  };

  const catalogByKey = Object.fromEntries(catalog.map((c) => [c.integration_key, c]));

  // ── actions (each calls a real endpoint) ──
  const createInstance = async () => {
    const row = createFor;
    await run('create', row.integration_key,
      () => jsonPost(request, '/api/v1/billing/me/connectors/instances', { integration_key: row.integration_key, nickname: nickname.trim() || null }),
      `Connection added for ${row.integration_display_name}.`);
    setCreateFor(null); setNickname('');
  };
  const enable = (inst) => run('enable', inst.instance_id, () => jsonPost(request, `/api/v1/billing/me/connectors/instances/${inst.instance_id}/enable`), 'Connector enabled.');
  const disable = (inst) => run('disable', inst.instance_id, () => jsonPost(request, `/api/v1/billing/me/connectors/instances/${inst.instance_id}/disable`), 'Connector disabled. Configuration and credentials retained.');
  const test = (inst) => run('test', inst.instance_id, () => jsonPost(request, `/api/v1/billing/me/connectors/instances/${inst.instance_id}/health-test`))
    .then((d) => { if (d) setToast({ ok: d.status === 'ready', msg: `Readiness: ${d.status}. ${d.note || ''}` }); });
  const remove = (inst) => run('delete', inst.instance_id, () => request(`/api/v1/billing/me/connectors/instances/${inst.instance_id}`, { method: 'DELETE' }), 'Connection removed. Stored credentials were purged.');

  // Retention selection (downgrade grace). retainable = effective allowance (number, or 'unlimited').
  const retainable = retention && retention.retainable_count;
  const retainCap = (retainable === 'unlimited' || retainable === -1) ? Infinity : Number(retainable || 0);
  const slotInstances = (retention?.integrations || []).filter((x) => x.slot_consuming);
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length < retainCap ? [...s, id] : s));
  const openSelect = () => { setSelected(slotInstances.filter((x) => x.enabled).slice(0, retainCap === Infinity ? undefined : retainCap).map((x) => x.instance_id)); setSelectOpen(true); };
  const submitSelection = async () => {
    const d = await run('retain', 'selection', () => jsonPost(request, '/api/v1/billing/me/connectors/retention-selection', { retained_instance_ids: selected }), 'Selection saved. Non-selected integrations were disabled (configuration and credentials preserved).');
    if (d) setSelectOpen(false);
  };

  const openEditor = (inst, kind) => {
    const row = catalogByKey[inst.integration_key];
    const schema = row?.config_schema || [];
    if (kind === 'config') {
      const init = {};
      (schema.filter((f) => !f.write_only)).forEach((f) => { init[f.field] = inst.config?.[f.field] ?? ''; });
      setFields(init);
    } else {
      setFields({});   // secrets always start blank (write-only, never prefilled)
    }
    setEditing({ instance: inst, kind, schema });
  };

  const submitEditor = async () => {
    const { instance, kind, schema } = editing;
    if (kind === 'config') {
      const body = buildConfigBody(schema, fields);   // non-secret fields only (secrets can't leak here)
      const d = await run('config', instance.instance_id, () => jsonPatch(request, `/api/v1/billing/me/connectors/instances/${instance.instance_id}`, body), 'Configuration saved.');
      if (d) setEditing(null);
    } else {
      const secrets = buildSecretsBody(schema, fields);   // write-only fields only; blanks omitted
      if (Object.keys(secrets).length === 0) { setToast({ ok: false, msg: 'Enter at least one credential value.' }); return; }
      const d = await run('secrets', instance.instance_id, () => jsonPost(request, `/api/v1/billing/me/connectors/instances/${instance.instance_id}/secrets`, { secrets }), 'Credentials saved securely (write-only).');
      if (d) setEditing(null);
    }
  };

  if (state === 'loading') return <Center icon={RefreshCw} spin title="Loading integrations…" />;
  if (state === 'expired') return <Center icon={Lock} title="Your session has expired." subtitle="Please sign in again." />;
  if (state === 'error') return <Center icon={AlertTriangle} title="Integrations are temporarily unavailable." action={<Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Retry</Button>} />;

  const slotsAllowedLabel = slots.allowed === 'unlimited' || slots.allowed === -1 ? 'Unlimited' : slots.allowed;
  const noFreeSlot = !(slots.allowed === 'unlimited' || slots.allowed === -1) && slots.used >= slots.allowed;

  return (
    <div className="space-y-4">
      {toast && <div className={`flex items-center gap-2 p-3 rounded-lg border ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">{toast.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}<span className="text-sm">{toast.msg}</span></div>}

      {/* Downgrade grace / over-limit banner — honest, actionable, real endpoint. */}
      {retention && retention.over_limit && (
        <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Your integration allowance has changed.</p>
              <p className="text-sm mt-0.5">
                Select the integrations you want to keep active
                {retention.grace_expires_at ? <> before <span className="font-medium">{new Date(retention.grace_expires_at).toLocaleDateString()}</span></> : null}.
                You have <span className="font-medium">{retention.active_slot_consuming_count}</span> active of{' '}
                <span className="font-medium">{slotsAllowedLabel}</span> allowed. Existing integrations keep running until you choose; nothing is deleted.
              </p>
              <Button size="sm" className="mt-2" onClick={openSelect}>Select integrations to retain</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-700">Active integration slots: <span className="font-semibold">{slots.used} of {slotsAllowedLabel}</span></p>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {/* Instances (configured connections) */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Your connections</CardTitle></CardHeader>
        <CardContent>
          {instances.length === 0 ? <p className="text-sm text-gray-500">No connections yet. Add one from an entitled connector below.</p> : (
            <div className="space-y-2">
              {instances.map((inst) => {
                const secretStatus = inst.secret_status || [];
                const missing = secretStatus.filter((s) => !s.configured).map((s) => s.field);
                const b = (a) => busy === `${inst.instance_id}:${a}`;
                return (
                  <div key={inst.instance_id} className="border rounded p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{inst.instance_display_name || inst.integration_display_name}</p>
                        <p className="text-xs text-gray-500">{inst.integration_display_name}{inst.provider_display_name ? ` · ${inst.provider_display_name}` : ''}</p>
                      </div>
                      <Badge className={inst.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{inst.enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </div>
                    {/* secret STATUS only — never a value */}
                    {secretStatus.length > 0 && (
                      <p className="text-xs mt-1 text-gray-500">Credentials: {missing.length === 0 ? <span className="text-green-700">all set</span> : <span className="text-amber-700">{missing.length} missing</span>}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button size="sm" variant="outline" disabled={b('config')} onClick={() => openEditor(inst, 'config')}><Settings2 className="h-3 w-3 mr-1" />Configure</Button>
                      <Button size="sm" variant="outline" disabled={b('secrets')} onClick={() => openEditor(inst, 'secrets')}><KeyRound className="h-3 w-3 mr-1" />Credentials</Button>
                      {inst.enabled
                        ? <Button size="sm" variant="outline" disabled={b('disable')} onClick={() => disable(inst)}><Power className="h-3 w-3 mr-1" />Disable</Button>
                        : <Button size="sm" variant="outline" disabled={b('enable') || (noFreeSlot) || missing.length > 0} onClick={() => enable(inst)} title={missing.length > 0 ? 'Add all credentials first' : noFreeSlot ? 'No free integration slot' : ''}><Power className="h-3 w-3 mr-1" />Enable</Button>}
                      <Button size="sm" variant="outline" disabled={b('test')} onClick={() => test(inst)}><Activity className="h-3 w-3 mr-1" />Test</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" disabled={b('delete')} onClick={() => remove(inst)}><Trash2 className="h-3 w-3 mr-1" />Remove</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Catalog (available connectors) */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Available connectors</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {catalog.map((row) => {
              const entitled = row.entitlement_state === 'ENTITLED';
              return (
                <div key={row.integration_key} className="flex items-center justify-between border rounded px-3 py-2">
                  <div>
                    <p className="text-gray-900 flex items-center gap-1"><Plug className="h-3.5 w-3.5 text-gray-400" />{primaryLabel(row.integration_display_name, row.integration_key)}</p>
                    <p className="text-xs text-gray-500">{row.provider_display_name || row.category || ''}{row.instance_count ? ` · ${row.instance_count} connection${row.instance_count > 1 ? 's' : ''}` : ''}</p>
                  </div>
                  {entitled
                    ? <Button size="sm" variant="outline" disabled={!row.can_configure} title={!row.can_configure ? (noFreeSlot ? 'No free integration slot' : 'You do not have permission to configure connectors') : ''} onClick={() => { setCreateFor(row); setNickname(''); }}><Plus className="h-3 w-3 mr-1" />Add connection</Button>
                    : <span className="text-xs text-gray-600">{row.pricing_state === 'add_on' ? 'Add-on' : 'Upgrade'}</span>}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-3">Credential fields are write-only — values are never displayed, only whether a credential is set. No provider-global secret is ever shown.</p>
        </CardContent>
      </Card>

      {/* Create-connection modal */}
      <Dialog open={!!createFor} onOpenChange={(o) => { if (!o) { setCreateFor(null); setNickname(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add connection</DialogTitle>
            <DialogDescription>{createFor?.integration_display_name}. It is created disabled; add credentials, then enable it (a slot is consumed on enable).</DialogDescription>
          </DialogHeader>
          <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">Nickname (optional)</label>
          <input className="w-full border rounded px-2 py-1.5 text-sm" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. Primary account" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => { setCreateFor(null); setNickname(''); }}>Cancel</Button>
            <Button onClick={createInstance} disabled={busy === `${createFor?.integration_key}:create`}>Add connection</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Retention selection modal (downgrade grace). Real endpoint; states consequences honestly. */}
      <Dialog open={selectOpen} onOpenChange={(o) => { if (!o) setSelectOpen(false); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select integrations to retain</DialogTitle>
            <DialogDescription>
              Keep up to <span className="font-medium">{retainCap === Infinity ? 'Unlimited' : retainCap}</span> active integration{retainCap === 1 ? '' : 's'}.
              Selected integrations stay enabled; non-selected are <span className="font-medium">disabled, not deleted</span> — configuration and credentials are preserved and can be restored after you add capacity.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
            {slotInstances.map((x) => {
              const checked = selected.includes(x.instance_id);
              const atCap = !checked && selected.length >= retainCap;
              return (
                <label key={x.instance_id} className={`flex items-center justify-between border rounded px-3 py-2 ${atCap ? 'opacity-50' : 'cursor-pointer'}`}>
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={checked} disabled={atCap} onChange={() => toggleSelect(x.instance_id)} />
                    <span className="text-sm text-gray-900">{x.display_name}</span>
                    {!x.enabled && <span className="text-xs text-gray-400">(currently disabled)</span>}
                  </span>
                </label>
              );
            })}
            {slotInstances.length === 0 && <p className="text-sm text-gray-500">No slot-consuming integrations.</p>}
          </div>
          <p className="text-xs text-gray-500 mt-2">Selected {selected.length} of {retainCap === Infinity ? '∞' : retainCap}. Core / non-slot connectors are unaffected and never listed here.</p>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setSelectOpen(false)}>Cancel</Button>
            <Button onClick={submitSelection} disabled={busy === 'selection:retain' || selected.length === 0 || selected.length > retainCap}>Confirm selection</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Configure / Credentials editor */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.kind === 'secrets' ? 'Credentials' : 'Configuration'} — {editing?.instance?.instance_display_name}</DialogTitle>
            <DialogDescription>
              {editing?.kind === 'secrets'
                ? 'Credential values are write-only: they are stored encrypted and never displayed. Leave a field blank to keep the current value.'
                : 'Non-secret configuration only (hosts, ids, usernames). Secret fields are managed under Credentials.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {(editing?.schema || []).filter((f) => editing?.kind === 'secrets' ? f.write_only : !f.write_only).map((f) => (
              <div key={f.field}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{f.label || f.field}{f.required ? <span className="text-red-500"> *</span> : ''}</label>
                <input
                  type={f.write_only ? 'password' : 'text'}
                  autoComplete={f.write_only ? 'new-password' : 'off'}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={fields[f.field] ?? ''}
                  placeholder={f.write_only ? '•••••••• (write-only)' : ''}
                  onChange={(e) => setFields({ ...fields, [f.field]: e.target.value })} />
              </div>
            ))}
            {(editing?.schema || []).filter((f) => editing?.kind === 'secrets' ? f.write_only : !f.write_only).length === 0 && (
              <p className="text-sm text-gray-500">No {editing?.kind === 'secrets' ? 'credential' : 'configuration'} fields for this connector.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submitEditor} disabled={busy && busy.startsWith(editing?.instance?.instance_id)}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Center = ({ icon: Icon, spin, title, subtitle, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <Icon className={`h-7 w-7 text-gray-400 mb-2 ${spin ? 'animate-spin' : ''}`} />
    <p className="text-gray-800 font-medium">{title}</p>
    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);
